import { Link } from "react-router"

import type {
  AccountAnalyticsRow,
  AnalyticsTotals,
} from "@/api/hooks/dashboard"
import {
  DASHBOARD_ACCOUNT_COLUMNS,
  DASHBOARD_FIRST_COL_ACCOUNT,
} from "../lib/columns"
import { AnalyticsTableShell } from "./AnalyticsTableShell"

/**
 * Account-tab wrapper around `AnalyticsTableShell`. Pinned column
 * is `account_name`, and its link cell hops to the React Ads
 * Manager with the account ID + current date range + network
 * (`dashboard.html:2212-2226`).
 */

interface AccountAnalyticsTableProps {
  rows: AccountAnalyticsRow[]
  totals: AnalyticsTotals | null
  columnOrder: string[]
  sortKey: string
  sortDir: "asc" | "desc"
  onSort: (key: string, columnType: "string" | "number") => void
  onColumnOrderChange: (order: string[]) => void
  startDate: string
  endDate: string
  network: string
  isLoading?: boolean
}

function buildAdsManagerLink(
  advertiserId: string,
  startDate: string,
  endDate: string,
  network: string,
): string {
  const params = new URLSearchParams()
  params.set("account_id", String(advertiserId))
  if (startDate && endDate) {
    params.set("start_date", startDate)
    params.set("end_date", endDate)
  }
  if (network && network !== "all") params.set("network", network)
  return `/adsmanager?${params.toString()}`
}

export function AccountAnalyticsTable({
  rows,
  totals,
  columnOrder,
  sortKey,
  sortDir,
  onSort,
  onColumnOrderChange,
  startDate,
  endDate,
  network,
  isLoading,
}: AccountAnalyticsTableProps) {
  return (
    <AnalyticsTableShell<AccountAnalyticsRow>
      tableId="accountAnalyticsTable"
      columns={DASHBOARD_ACCOUNT_COLUMNS}
      pinnedColumnId={DASHBOARD_FIRST_COL_ACCOUNT}
      rows={rows}
      totals={totals}
      columnOrder={columnOrder}
      sortKey={sortKey}
      sortDir={sortDir}
      onSort={onSort}
      onColumnOrderChange={onColumnOrderChange}
      isLoading={isLoading}
      renderFirstCell={(row) => (
        <span className="inline-flex min-w-0 max-w-full items-center gap-2">
          <Link
            to={buildAdsManagerLink(
              row.advertiser_id,
              startDate,
              endDate,
              network,
            )}
            className="min-w-0 truncate text-[#e0e0e0] hover:text-[#8b9aff] hover:underline underline-offset-2"
          >
            {row.account_name || row.advertiser_id}
          </Link>
          {row.deleted_at ? (
            <span className="inline-flex shrink-0 items-center rounded-full border border-gray-600/40 bg-gray-800/60 px-2 py-0.5 text-[10px] font-semibold text-gray-300">
              Deleted
            </span>
          ) : null}
        </span>
      )}
      renderFirstTotalsCell={() => (
        <strong className="text-[#e0e0e0]">Totals</strong>
      )}
    />
  )
}
